import type { FeatureFlagKey } from './feature-flag-key.ts';

export interface ResolvedFlag {
  key: string;
  name: string;
  enabled: boolean;
}

export interface FeatureFlagsOptions {
  apiUrl?: string;
  /**
   * The active workspace id. Required for workspace-scoped flags to resolve;
   * pass null when there is no workspace context (workspace-scoped flags then
   * resolve to false).
   */
  workspaceId?: string | null;
}

const DEFAULT_API_URL = 'http://localhost:3002/graphql';

const RESOLVED_FLAGS_QUERY = `
  query ResolvedFlags($workspaceId: ID) {
    resolvedFlags(workspaceId: $workspaceId) {
      key
      name
      enabled
    }
  }
`;

export class FeatureFlagsClient {
  private flags: Map<string, boolean> | null = null;
  private loadedFor: string | null | undefined = undefined;
  /**
   * Bumped by every `init` and every `reset`. `init` is async and nothing
   * cancels its fetch, so without this a response can land AFTER the client
   * was reset or re-pointed and write a flag set the app has already left:
   * the old workspace's flags, stamped as loaded for the new one. A response
   * whose generation is stale is dropped.
   */
  private generation = 0;

  get initialized(): boolean {
    return this.flags !== null;
  }

  /**
   * The workspace id the currently-resolved flags were loaded against
   * (`undefined` until the first init). Lets consumers detect a stale set
   * after a workspace switch without re-initializing on every remount.
   */
  get loadedWorkspaceId(): string | null | undefined {
    return this.loadedFor;
  }

  async init(bearerToken: string, options?: FeatureFlagsOptions): Promise<void> {
    const generation = ++this.generation;
    const apiUrl = options?.apiUrl ?? DEFAULT_API_URL;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${bearerToken}`,
      },
      body: JSON.stringify({
        query: RESOLVED_FLAGS_QUERY,
        variables: { workspaceId: options?.workspaceId ?? null },
      }),
    });

    if (!response.ok) {
      throw new Error(`Feature flags API returned ${response.status}`);
    }

    const json = (await response.json()) as {
      errors?: { message: string }[];
      data: { resolvedFlags: ResolvedFlag[] };
    };

    const firstError = json.errors?.[0];
    if (firstError) {
      throw new Error(`Feature flags GraphQL error: ${firstError.message}`);
    }

    // A reset, or a newer init, happened while this request was in flight —
    // its answer describes a state the app has left. Throwing here would
    // reject a caller that is no longer interested, so it just stops.
    if (generation !== this.generation) return;

    const resolvedFlags: ResolvedFlag[] = json.data.resolvedFlags;
    this.flags = new Map(resolvedFlags.map((f) => [f.key, f.enabled]));
    this.loadedFor = options?.workspaceId ?? null;
  }

  isFeatureFlagEnabled(key: FeatureFlagKey): boolean {
    if (!this.flags) {
      throw new Error('FeatureFlags has not been initialized. Call FeatureFlags.init() first.');
    }
    return this.flags.get(key) ?? false;
  }

  reset(): void {
    this.generation += 1;
    this.flags = null;
    this.loadedFor = undefined;
  }
}

/** Module-level singleton shared by every consumer in a process. */
export const FeatureFlags = new FeatureFlagsClient();
